import { Sun, Wind, Zap, Globe, Activity, Wrench, TrendingUp, Award } from 'lucide-react';
import SectionReveal from './SectionReveal';

const iconMap = { Sun, Wind, Zap, Globe, Activity, Wrench, TrendingUp, Award };
const RENEWABLE_IMG = "https://images.unsplash.com/photo-1765263857986-271b4923632d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHx3aW5kJTIwdHVyYmluZSUyMHNvbGFyJTIwcGFuZWxzfGVufDB8fHx8MTc3NDc5NzI3N3ww&ixlib=rb-4.1.0&q=85";

function FeatureCard({ card, index }) {
  const Icon = iconMap[card.icon];
  return (
    <SectionReveal key={card.title} delay={index * 100}>
      <div className="group bg-[#111111] border border-white/10 p-7 hover:border-[#D4A017]/35 hover:-translate-y-1 transition-all duration-300 h-full">
        <div className="mb-6 w-11 h-11 flex items-center justify-center border border-[#D4A017]/30 text-[#D4A017] group-hover:bg-[#D4A017]/10 transition-colors duration-200">
          {Icon && <Icon size={20} />}
        </div>
        <h3 className="text-lg font-medium text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{card.title}</h3>
        <p className="text-sm text-white/55 leading-relaxed">{card.body}</p>
      </div>
    </SectionReveal>
  );
}

function SectionHeader({ label, title, subtitle }) {
  return (
    <SectionReveal>
      <div className="mb-16">
        <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium">{label}</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-5 max-w-3xl" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h2>
        {subtitle && <p className="text-base text-white/60 max-w-2xl leading-relaxed">{subtitle}</p>}
      </div>
    </SectionReveal>
  );
}

function FlowStep({ step, index }) {
  const isHighlighted = step.step === '03' || step.step === '04';
  return (
    <div className="relative flex flex-col items-center text-center p-6 group">
      <div
        className={`w-16 h-16 flex items-center justify-center border-2 mb-5 transition-colors duration-300 z-10 bg-[#0a0a0a] ${
          isHighlighted ? 'border-[#D4A017] text-[#D4A017]' : 'border-white/20 text-white/40 group-hover:border-[#D4A017]/50 group-hover:text-[#D4A017]/70'
        }`}
        data-testid={`om-step-${index}`}
      >
        <span className="text-sm font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>{step.step}</span>
      </div>
      <h4 className={`text-sm font-medium mb-2 tracking-wide ${isHighlighted ? 'text-[#D4A017]' : 'text-white/70'}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
        {step.label}
      </h4>
      <p className="text-xs text-white/40 leading-relaxed max-w-[160px]">{step.desc}</p>
    </div>
  );
}

export default function RenewableSection({ text }) {
  const om = text.om;
  return (
    <section id="renewable" className="bg-[#0a0a0a]">
      <div className="py-24 sm:py-32">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <SectionHeader label={text.label} title={text.title} subtitle={text.subtitle} />
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-5 mb-12">
            {text.cards.map((card, i) => <FeatureCard key={card.title} card={card} index={i} />)}
          </div>
          <SectionReveal delay={300}>
            <div className="relative overflow-hidden">
              <img src={RENEWABLE_IMG} alt="Renewable Energy" className="w-full h-64 sm:h-80 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/40 to-transparent" />
              <div className="absolute inset-0 flex items-center px-8 sm:px-14">
                <p className="text-2xl sm:text-3xl font-medium text-white max-w-lg leading-snug" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  3+ GW of renewable energy<br /><span className="text-[#D4A017]">under active development</span>
                </p>
              </div>
            </div>
          </SectionReveal>
        </div>
      </div>
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <div className="h-px bg-gradient-to-r from-transparent via-[#D4A017]/30 to-transparent" />
      </div>
      <div className="py-24 sm:py-32">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <SectionHeader label={om.label} title={om.title} subtitle={om.subtitle} />
          <SectionReveal delay={100}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 mb-16 relative">
              <div className="absolute top-8 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-white/10 via-[#D4A017]/30 to-white/10 hidden lg:block" />
              {om.flow.map((step, i) => <FlowStep key={step.step} step={step} index={i} />)}
            </div>
          </SectionReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {om.services.map((service, i) => <FeatureCard key={service.title} card={service} index={i} />)}
          </div>
        </div>
      </div>
    </section>
  );
}
